import { EditorCommandType } from './rich-text-editor-command.service';

export interface IFormatStyle {
  value: string;
  label: string;
}

export interface IEditorCommandConfig {
  key: string;
  command: EditorCommandType;
  label: string;
  icon?: string;
  shortcut?: string;
  requiresValue?: boolean;
  handler?: string;
}

export const ALIGNMENT_COMMANDS = [
  'justifyLeft',
  'justifyCenter',
  'justifyRight',
  'justifyFull',
] as const;

export const BLOCK_ELEMENTS = 'p, div, h1, h2, h3, h4, h5, h6, blockquote, pre';

export const EDITOR_COMMANDS = {
  TEXT_FORMATTING: [
    {
      key: 'bold',
      command: 'bold' as EditorCommandType,
      label: 'Negrito',
      icon: 'format_bold',
      shortcut: 'Ctrl+B',
      handler: 'formatBold',
    },
    {
      key: 'italic',
      command: 'italic' as EditorCommandType,
      label: 'Itálico',
      icon: 'format_italic',
      shortcut: 'Ctrl+I',
      handler: 'formatItalic',
    },
    {
      key: 'underline',
      command: 'underline' as EditorCommandType,
      label: 'Sublinhado',
      icon: 'format_underlined',
      shortcut: 'Ctrl+U',
      handler: 'formatUnderline',
    },
    {
      key: 'strikethrough',
      command: 'strikethrough' as EditorCommandType,
      label: 'Tachado',
      icon: 'format_strikethrough',
      handler: 'formatStrikethrough',
    },
  ] as IEditorCommandConfig[],

  LIST_FORMATTING: [
    {
      key: 'insertUnorderedList',
      command: 'insertUnorderedList' as EditorCommandType,
      label: 'Lista não ordenada',
      icon: 'format_list_bulleted',
      handler: 'formatUnorderedList',
    },
    {
      key: 'insertOrderedList',
      command: 'insertOrderedList' as EditorCommandType,
      label: 'Lista ordenada',
      icon: 'format_list_numbered',
      handler: 'formatOrderedList',
    },
  ] as IEditorCommandConfig[],

  ALIGNMENT: [
    {
      key: 'justifyLeft',
      command: 'justifyLeft' as EditorCommandType,
      label: 'Alinhar à esquerda',
      icon: 'format_align_left',
      handler: 'formatAlignLeft',
    },
    {
      key: 'justifyCenter',
      command: 'justifyCenter' as EditorCommandType,
      label: 'Centralizar',
      icon: 'format_align_center',
      handler: 'formatAlignCenter',
    },
    {
      key: 'justifyRight',
      command: 'justifyRight' as EditorCommandType,
      label: 'Alinhar à direita',
      icon: 'format_align_right',
      handler: 'formatAlignRight',
    },
    {
      key: 'justifyFull',
      command: 'justifyFull' as EditorCommandType,
      label: 'Justificar',
      icon: 'format_align_justify',
      handler: 'formatAlignJustify',
    },
  ] as IEditorCommandConfig[],

  BLOCK_FORMATTING: [
    {
      key: 'p',
      command: 'formatBlock' as EditorCommandType,
      label: 'Parágrafo',
      handler: 'formatParagraph',
    },
    {
      key: 'h1',
      command: 'formatBlock' as EditorCommandType,
      label: 'Título 1',
      handler: 'formatHeading1',
    },
    {
      key: 'h2',
      command: 'formatBlock' as EditorCommandType,
      label: 'Título 2',
      handler: 'formatHeading2',
    },
    {
      key: 'h3',
      command: 'formatBlock' as EditorCommandType,
      label: 'Título 3',
      handler: 'formatHeading3',
    },
    {
      key: 'h4',
      command: 'formatBlock' as EditorCommandType,
      label: 'Título 4',
      handler: 'formatHeading4',
    },
    {
      key: 'h5',
      command: 'formatBlock' as EditorCommandType,
      label: 'Título 5',
      handler: 'formatHeading5',
    },
    {
      key: 'h6',
      command: 'formatBlock' as EditorCommandType,
      label: 'Título 6',
      handler: 'formatHeading6',
    },
    {
      key: 'blockquote',
      command: 'formatBlock' as EditorCommandType,
      label: 'Citação',
      handler: 'formatBlockquote',
    },
  ] as IEditorCommandConfig[],

  COLOR: [
    {
      key: 'foreColor',
      command: 'foreColor' as EditorCommandType,
      label: 'Cor do texto',
      icon: 'format_color_text',
      requiresValue: true,
      handler: 'applyTextColor',
    },
    {
      key: 'backColor',
      command: 'backColor' as EditorCommandType,
      label: 'Cor de fundo',
      icon: 'format_color_fill',
      requiresValue: true,
      handler: 'applyBackgroundColor',
    },
  ] as IEditorCommandConfig[],

  LINK: [
    {
      key: 'createLink',
      command: 'createLink' as EditorCommandType,
      label: 'Inserir link',
      icon: 'insert_link',
      requiresValue: true,
      handler: 'insertLink',
    },
    {
      key: 'unlink',
      command: 'unlink' as EditorCommandType,
      label: 'Remover link',
      icon: 'link_off',
      handler: 'removeLink',
    },
  ] as IEditorCommandConfig[],

  UTILITY: [
    {
      key: 'removeFormat',
      command: 'removeFormat' as EditorCommandType,
      label: 'Remover formatação',
      icon: 'format_clear',
      handler: 'removeFormat',
    },
    {
      key: 'undo',
      command: 'undo' as EditorCommandType,
      label: 'Desfazer',
      icon: 'undo',
      shortcut: 'Ctrl+Z',
      handler: 'undo',
    },
    {
      key: 'redo',
      command: 'redo' as EditorCommandType,
      label: 'Refazer',
      icon: 'redo',
      shortcut: 'Ctrl+Y',
      handler: 'redo',
    },
  ] as IEditorCommandConfig[],
};

export const FORMAT_STYLES: readonly IFormatStyle[] = [
  { value: 'p', label: 'Normal' },
  { value: 'blockquote', label: 'Citação' },
  { value: 'h1', label: 'Título 1' },
  { value: 'h2', label: 'Título 2' },
  { value: 'h3', label: 'Título 3' },
  { value: 'h4', label: 'Título 4' },
  { value: 'h5', label: 'Título 5' },
  { value: 'h6', label: 'Título 6' },
] as const;

export const COLOR_PALETTE: readonly string[] = [
  '#000000',
  '#424242',
  '#636363',
  '#9C9C94',
  '#CEC6CE',
  '#EFEFEF',
  '#F7F7F7',
  '#FFFFFF',
  '#FF0000',
  '#FF9C00',
  '#FFFF00',
  '#00FF00',
  '#00FFFF',
  '#0000FF',
  '#9C00FF',
  '#FF00FF',
  '#F7C6CE',
  '#FFE7CE',
  '#FFEFC6',
  '#D6EFD6',
  '#CEDEE7',
  '#CEE7F7',
  '#D6D6E7',
  '#E7D6DE',
  '#E79C9C',
  '#FFC69C',
  '#FFE79C',
  '#B5D6A5',
  '#A5C6CE',
  '#9CC6EF',
  '#B5A5D6',
  '#D6A5BD',
  '#E76363',
  '#F7AD6B',
  '#FFD663',
  '#94BD7B',
  '#73A5AD',
  '#6BADDE',
  '#8C7BC6',
  '#C67BA5',
  '#CE0000',
  '#E79439',
  '#EFC631',
  '#6BA54A',
  '#4A7B8C',
  '#3984C6',
  '#634AA5',
  '#A54A7B',
  '#9C0000',
  '#B56308',
  '#BD9400',
  '#397B21',
  '#104A5A',
  '#085294',
  '#311873',
  '#731842',
  '#630000',
  '#7B3900',
  '#846300',
  '#295218',
  '#083139',
  '#003163',
  '#21104A',
  '#4A1031',
] as const;
